import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X,
  TrendingDown,
  DollarSign,
  Target,
  Eye,
  Clock,
  Filter,
  Check,
  RefreshCw
} from 'lucide-react';

interface Alert {
  id: number;
  alert_type: string;
  severity: 'info' | 'warning' | 'critical';
  campaign_id?: number;
  keyword_id?: number;
  title: string;
  message: string;
  metric_name?: string;
  metric_value?: number;
  threshold_value?: number;
  is_read: boolean;
  is_resolved: boolean;
  created_at: string;
}

interface AlertsPanelProps {
  campaignId?: string;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ campaignId }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'critical'>('unread');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  useEffect(() => {
    loadAlerts();
    // Odświeżaj alerty co 5 minut
    const interval = setInterval(loadAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [campaignId, filter]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (campaignId) params.append('campaign_id', campaignId);
      if (filter !== 'all') params.append('filter', filter);

      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/alerts?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId: number) => {
    try {
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/alerts/${alertId}/read`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setAlerts(prev => prev.map(a => 
          a.id === alertId ? { ...a, is_read: true } : a
        ));
      }
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const resolveAlert = async (alertId: number) => {
    try {
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/alerts/${alertId}/resolve`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setAlerts(prev => prev.map(a => 
          a.id === alertId ? { ...a, is_resolved: true } : a
        ));
        setSelectedAlert(null);
      }
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'from-red-900/30 to-red-700/30 border-red-500/30';
      case 'warning': return 'from-yellow-900/30 to-yellow-700/30 border-yellow-500/30';
      default: return 'from-blue-900/30 to-blue-700/30 border-blue-500/30';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      default: return <Bell className="w-5 h-5 text-blue-400" />;
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'LOW_CTR': return <TrendingDown className="w-5 h-5" />;
      case 'HIGH_CPC': return <DollarSign className="w-5 h-5" />;
      case 'NO_CONVERSION': return <Target className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Przed chwilą';
    if (hours < 24) return `${hours}h temu`;
    if (hours < 48) return 'Wczoraj';
    return date.toLocaleDateString('pl-PL');
  };

  const unreadCount = alerts.filter(a => !a.is_read && !a.is_resolved).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical' && !a.is_resolved).length;

  return (
    <div className="space-y-6">
      {/* Header z statystykami */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Alerty i powiadomienia</h2>
            <p className="text-white/60 text-sm">
              {unreadCount > 0 ? `${unreadCount} nieprzeczytanych` : 'Wszystkie przeczytane'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filtry */}
          <div className="flex items-center gap-2 p-1 rounded-lg bg-white/5 border border-white/10">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                filter === 'all' 
                  ? 'bg-purple-600/20 text-purple-400' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Wszystkie
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                filter === 'unread' 
                  ? 'bg-purple-600/20 text-purple-400' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Nieprzeczytane
              {unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-purple-500 text-white text-xs">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setFilter('critical')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                filter === 'critical' 
                  ? 'bg-purple-600/20 text-purple-400' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Krytyczne
              {criticalCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs">
                  {criticalCount}
                </span>
              )}
            </button>
          </div>

          <button
            onClick={loadAlerts}
            disabled={loading}
            className="p-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Lista alertów */}
      <div className="space-y-3">
        {loading && alerts.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-white/40 mx-auto mb-3" />
            <p className="text-white/60">Ładowanie alertów...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-12 rounded-xl bg-white/5 border border-white/10">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-white/60">Brak nowych alertów</p>
            <p className="text-white/40 text-sm mt-1">Wszystko działa prawidłowo</p>
          </div>
        ) : (
          <AnimatePresence>
            {alerts.map((alert) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`
                  relative overflow-hidden rounded-xl p-4
                  bg-gradient-to-br ${getSeverityColor(alert.severity)}
                  backdrop-blur-xl border
                  hover:shadow-lg transition-all cursor-pointer
                  ${!alert.is_read ? 'ring-2 ring-purple-500/30' : ''}
                  ${alert.is_resolved ? 'opacity-60' : ''}
                `}
                onClick={() => {
                  if (!alert.is_read) markAsRead(alert.id);
                  setSelectedAlert(alert);
                }}
              >
                {/* Nowy alert indicator */}
                {!alert.is_read && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                )}

                <div className="flex items-start gap-4">
                  {/* Ikona */}
                  <div className="p-2 rounded-lg bg-white/10">
                    {getSeverityIcon(alert.severity)}
                  </div>

                  {/* Treść */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-white flex items-center gap-2">
                          {alert.title}
                          {alert.is_resolved && (
                            <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">
                              Rozwiązany
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-white/70 mt-1">{alert.message}</p>
                        
                        {/* Metryki */}
                        {alert.metric_name && (
                          <div className="flex items-center gap-4 mt-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/50">Wartość:</span>
                              <span className="text-sm font-medium text-white">
                                {alert.metric_value}
                                {alert.metric_name === 'ctr' && '%'}
                                {alert.metric_name === 'avg_cpc' && ' PLN'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/50">Próg:</span>
                              <span className="text-sm font-medium text-white/70">
                                {alert.threshold_value}
                                {alert.metric_name === 'ctr' && '%'}
                                {alert.metric_name === 'avg_cpc' && ' PLN'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-white/50 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(alert.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Modal szczegółów alertu */}
      <AnimatePresence>
        {selectedAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedAlert(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-2xl p-6 max-w-lg w-full border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${
                    selectedAlert.severity === 'critical' ? 'from-red-500 to-red-600' :
                    selectedAlert.severity === 'warning' ? 'from-yellow-500 to-yellow-600' :
                    'from-blue-500 to-blue-600'
                  }`}>
                    {getAlertIcon(selectedAlert.alert_type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedAlert.title}</h3>
                    <p className="text-sm text-white/50">{formatDate(selectedAlert.created_at)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-white/80">{selectedAlert.message}</p>

                {selectedAlert.metric_name && (
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-white/50 mb-1">Metryka</p>
                        <p className="font-medium text-white">{selectedAlert.metric_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50 mb-1">Obecna wartość</p>
                        <p className="font-medium text-white">
                          {selectedAlert.metric_value}
                          {selectedAlert.metric_name === 'ctr' && '%'}
                          {selectedAlert.metric_name === 'avg_cpc' && ' PLN'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50 mb-1">Próg alertu</p>
                        <p className="font-medium text-white/70">
                          {selectedAlert.threshold_value}
                          {selectedAlert.metric_name === 'ctr' && '%'}
                          {selectedAlert.metric_name === 'avg_cpc' && ' PLN'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50 mb-1">Ważność</p>
                        <p className={`font-medium ${
                          selectedAlert.severity === 'critical' ? 'text-red-400' :
                          selectedAlert.severity === 'warning' ? 'text-yellow-400' :
                          'text-blue-400'
                        }`}>
                          {selectedAlert.severity === 'critical' ? 'Krytyczna' :
                           selectedAlert.severity === 'warning' ? 'Ostrzeżenie' :
                           'Informacja'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sugerowane akcje */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-white/70">Sugerowane akcje:</p>
                  <div className="space-y-2">
                    {selectedAlert.alert_type === 'LOW_CTR' && (
                      <>
                        <button className="w-full p-3 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 transition-colors text-sm text-left">
                          🎯 Ulepsz teksty reklam
                        </button>
                        <button className="w-full p-3 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 transition-colors text-sm text-left">
                          🔍 Dodaj negatywne słowa kluczowe
                        </button>
                      </>
                    )}
                    {selectedAlert.alert_type === 'HIGH_CPC' && (
                      <>
                        <button className="w-full p-3 rounded-lg bg-yellow-600/20 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-600/30 transition-colors text-sm text-left">
                          💰 Obniż stawki CPC
                        </button>
                        <button className="w-full p-3 rounded-lg bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 transition-colors text-sm text-left">
                          📊 Popraw Quality Score
                        </button>
                      </>
                    )}
                    {selectedAlert.alert_type === 'NO_CONVERSION' && (
                      <>
                        <button className="w-full p-3 rounded-lg bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 transition-colors text-sm text-left">
                          ⏸️ Wstrzymaj słowo kluczowe
                        </button>
                        <button className="w-full p-3 rounded-lg bg-orange-600/20 border border-orange-500/30 text-orange-400 hover:bg-orange-600/30 transition-colors text-sm text-left">
                          🔄 Zmień stronę docelową
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Przyciski akcji */}
                <div className="flex gap-3 pt-4">
                  {!selectedAlert.is_resolved && (
                    <button
                      onClick={() => resolveAlert(selectedAlert.id)}
                      className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium hover:shadow-lg transition-all"
                    >
                      <Check className="w-4 h-4 inline mr-2" />
                      Oznacz jako rozwiązany
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedAlert(null)}
                    className="flex-1 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white font-medium hover:bg-white/20 transition-all"
                  >
                    Zamknij
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AlertsPanel;
